/**
 * W2.1 hero video facade — rendition downshift for deferred-media videos.
 *
 * The Liquid drop only exposes the top mp4 rendition, so snippets/video-facade
 * ships its <source> inert (data-src, no src) and this script decides what to
 * load: it reads the HLS master manifest Shopify generates next to every
 * video (a tiny text file listing every rendition), picks the largest one
 * whose height is <= 720px, maps its variant URI back to the matching mp4
 * URL, and promotes it into the <source>. On any failure (manifest missing,
 * unparseable, derived URL 404s) it falls back to the Liquid-exposed mp4.
 *
 * Zero video bytes move before this script assigns a src, and it never
 * initiates playback on its own for videos DeferredMedia has not cloned out
 * of their <template> (so the prefers-reduced-motion gate in global.js keeps
 * the hero poster-only).
 */
(function () {
  if (window.__videoFacadeInit) return;
  window.__videoFacadeInit = true;

  var MAX_HEIGHT = 720;
  var manifestCache = {};

  // <hash>.HD-720p.4.5Mbps.hls.<id>_.m3u8 -> <hash>.HD-720p-4.5Mbps-<id>.mp4
  function mp4FromVariant(manifestUrl, variantUri) {
    var m = variantUri.match(
      /^(.*?)((?:HD|SD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/
    );
    if (!m) return null;
    var file = m[1] + m[2] + "-" + m[3] + "-" + m[4] + ".mp4";
    try {
      return new URL(file, manifestUrl).href;
    } catch (err) {
      return null;
    }
  }

  function pickRendition(manifestText, manifestUrl) {
    var lines = manifestText.split(/\r?\n/);
    var best = null;
    for (var i = 0; i < lines.length; i += 1) {
      if (lines[i].indexOf("#EXT-X-STREAM-INF") !== 0) continue;
      var res = lines[i].match(/RESOLUTION=(\d+)x(\d+)/);
      var uri = (lines[i + 1] || "").trim();
      if (!res || !uri || uri.charAt(0) === "#") continue;
      var height = parseInt(res[2], 10);
      if (height > MAX_HEIGHT) continue;
      if (!best || height > best.height) best = { height: height, uri: uri };
    }
    return best ? mp4FromVariant(manifestUrl, best.uri) : null;
  }

  function resolveRendition(mp4Url) {
    // data-src is protocol-relative (//domain/...): absolutize it first or
    // both the manifest fetch and new URL() resolution fail
    var absolute;
    try {
      absolute = new URL(mp4Url, window.location.href).href;
    } catch (err) {
      return Promise.resolve(null);
    }
    var clean = absolute.replace(/[?#].*$/, "");
    var manifestUrl = clean.replace(
      /\.(?:HD|SD)-\d+p-[\d.]+Mbps-\d+\.mp4$/,
      ".m3u8"
    );
    if (manifestUrl === clean) return Promise.resolve(null);
    if (!manifestCache[manifestUrl]) {
      manifestCache[manifestUrl] = fetch(manifestUrl)
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (text) {
          return text ? pickRendition(text, manifestUrl) : null;
        })
        .catch(function () {
          return null;
        });
    }
    return manifestCache[manifestUrl];
  }

  function playMuted(video) {
    video.muted = true;
    var played = video.play();
    if (played && typeof played.catch === "function") {
      played.catch(function () {});
    }
  }

  // Activate a video DeferredMedia has already cloned into the document.
  function activateLive(video, src, fallback) {
    if (video.__facadeActivated) return;
    video.__facadeActivated = true;

    var source = video.querySelector("source");
    if (!source) return;

    // Template rewrite won the race and the clone is already playing it.
    if (
      source.getAttribute("src") === src &&
      (video.currentTime > 0 || !video.paused)
    ) {
      return;
    }

    if (src !== fallback) {
      var swapToFallback = function () {
        video.removeEventListener("error", swapToFallback, true);
        source.setAttribute("src", fallback);
        video.load();
        playMuted(video);
      };
      // capture: <source> error events do not bubble
      video.addEventListener("error", swapToFallback, true);
    }

    source.setAttribute("src", src);
    video.load();
    playMuted(video);
  }

  function setup(deferredMedia) {
    if (deferredMedia.__facadeSetup) return;
    deferredMedia.__facadeSetup = true;

    var template = deferredMedia.querySelector("template");
    if (!template || !template.content) return;
    var templateVideo = template.content.querySelector(
      "video[data-video-facade]"
    );
    if (!templateVideo) return;
    var templateSource = templateVideo.querySelector("source[data-src]");
    if (!templateSource) return;

    var fallback = templateSource.getAttribute("data-src");
    if (!fallback) return;
    var liquidHeight =
      parseInt(templateSource.getAttribute("data-height") || "0", 10) || 0;

    var ready;
    if (liquidHeight > 0 && liquidHeight <= MAX_HEIGHT) {
      // Liquid already found a small-enough rendition; no manifest needed.
      ready = Promise.resolve(fallback);
    } else {
      ready = resolveRendition(fallback).then(function (url) {
        return url || fallback;
      });
    }

    var apply = function (src) {
      // Future clones pick the src straight out of the template.
      templateSource.setAttribute("src", src);
      // A clone DeferredMedia inserted before we resolved.
      var live = deferredMedia.querySelector("video[data-video-facade]");
      if (live) activateLive(live, src, fallback);
    };

    ready.then(apply);
    deferredMedia.addEventListener("deferred-loaded", function () {
      ready.then(apply);
    });
  }

  function init() {
    var medias = document.querySelectorAll("deferred-media");
    for (var i = 0; i < medias.length; i += 1) setup(medias[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", init);
})();
